import { Logic } from '~common/ui-logic/logic'
import type {
    PersistedImportState,
    LanguageKey,
} from '~/features/email-importer/services/types'
import type { TaskState } from '~/services/cache/types'
import { executeTask } from '~common/ui-logic/tasks'
import { DEFAULT_INVOICE_KEYWORDS } from '~common/invoice-processing/crude-classification'
import type { AppContextType } from '~/app-context'
import {
    DEFAULT_PAGE_SIZE,
    getDefaultImporterDateRange,
} from '../../services/constants'

export interface ImporterLogicDeps {
    onClose: () => void
}

export interface ImporterState extends PersistedImportState {
    loadState: TaskState
    importerChangeState: TaskState
    areSettingsShown: boolean
    textFieldContent: string
}

export class ImporterLogic extends Logic<
    ImporterLogicDeps & AppContextType,
    ImporterState
> {
    getInitialState(): ImporterState {
        // Start with empty/default values, terms will be set in initialize()
        return {
            startedAt: null,
            lastUpdatedAt: null,
            nextPageToken: undefined,
            status: {
                paused: false,
                scan: 'idle',
                import: 'idle',
            },
            config: {
                terms: '',
                dateRange: getDefaultImporterDateRange(),
                selectedLanguages: [],
                pageSize: DEFAULT_PAGE_SIZE,
            },
            totalInvoiceCandidates: 0,
            invoiceCandidatesSeenThisRun: 0,
            actualInvoicesFoundThisRun: 0,
            invoiceCandidatesProcessedThisRun: 0,
            lastMessageTimestamp: null,
            errorMessage: null,
            loadState: 'pristine',
            importerChangeState: 'pristine',
            areSettingsShown: false,
            textFieldContent: '',
        }
    }

    private generateTermsFromLanguages(languages: LanguageKey[]): string[] {
        const termsSet = new Set<string>()

        // Add terms for each selected language
        languages.forEach((lang) => {
            DEFAULT_INVOICE_KEYWORDS[lang].forEach((term) => {
                termsSet.add(term)
            })
        })

        // Convert set to sorted array
        return Array.from(termsSet).sort()
    }

    private async saveSearchTermsToSettings(terms: string): Promise<void> {
        if (!this.deps.storageModules) return

        const user = await this.deps.storageModules.auth.getCurrentUser()
        if (!user) return

        const currentSettings = await this.deps.services.cache.getSettings()

        await this.deps.storageModules.auth.updateUser(user.id, {
            settings: {
                ...currentSettings,
                searchTerms: terms,
            },
        })

        this.deps.services.cache.updateSettings({
            ...currentSettings,
            searchTerms: terms,
        })
    }

    async initialize(): Promise<void> {
        await executeTask(this, 'loadState', async () => {
            // Load existing importer stats from local storage
            const existingState = await this.deps.services.importer.getState()

            let finalConfig = { ...existingState.config }

            // Simple terms initialization flow
            if (
                !existingState.config?.terms ||
                existingState.config.terms.length === 0
            ) {
                // Terms array is empty, check selectedLanguages and generate terms
                if (
                    existingState.config?.selectedLanguages &&
                    existingState.config.selectedLanguages.length > 0
                ) {
                    const terms = this.generateTermsFromLanguages(
                        existingState.config.selectedLanguages,
                    )
                    finalConfig = {
                        ...existingState.config,
                        terms: terms.join(' OR '),
                    }
                } else {
                    // No languages selected, use English as default
                    const terms = this.generateTermsFromLanguages(['en'])
                    finalConfig = {
                        ...existingState.config,
                        terms: terms.join(' OR '),
                        selectedLanguages: ['en'],
                    }
                }
            }
            const importStats =
                this.deps.services.cache.getKey('importProgress')

            const newState = {
                startedAt: existingState.startedAt,
                lastUpdatedAt: existingState.lastUpdatedAt,
                nextPageToken: existingState.nextPageToken,
                lastMessageTimestamp: importStats?.lastMessageTimestamp,
                status: existingState.status,
                totalInvoicesSeen: importStats?.totalInvoiceCandidates,
                invoicesSeenThisRun: importStats?.invoiceCandidatesSeenThisRun,
                config: finalConfig,
                textFieldContent: finalConfig.terms.split(' ').join('\n'),
                errorMessage: existingState.errorMessage,
            }

            // Set the final state with proper config
            this.setState(newState)
        })
    }

    start = async (): Promise<void> => {
        await executeTask(this, 'importerChangeState', async () => {
            await this.deps.services.importer.start({
                terms: this.state.config.terms,
                dateRange: this.state.config.dateRange,
                selectedLanguages: this.state.config.selectedLanguages,
                pageSize: this.state.config.pageSize,
                resetProgress: true,
            })
        })
        this.deps.onClose()
    }

    pause = async (): Promise<void> => {
        await executeTask(this, 'importerChangeState', async () => {
            await this.deps.services.importer.pause()
        })
    }

    stop = async (): Promise<void> => {
        await executeTask(this, 'importerChangeState', async () => {
            await this.deps.services.importer.reset()
        })
    }

    reset = async (): Promise<void> => {
        // await this.deps.services.ipc.importer.reset()
    }

    updateFromYear = (year: string): void => {
        const yearNum = parseInt(year, 10)
        const newDateRange = {
            ...this.state.config.dateRange,
            fromYear: yearNum,
        }
        const newConfig = {
            ...this.state.config,
            dateRange: newDateRange,
        }
        this.setState({
            config: newConfig,
        })
    }

    updateFromMonth = (month: string): void => {
        const monthNum = parseInt(month, 10)
        const newDateRange = {
            ...this.state.config.dateRange,
            fromMonth: monthNum,
        }
        const newConfig = {
            ...this.state.config,
            dateRange: newDateRange,
        }
        this.setState({
            config: newConfig,
        })
    }

    updateToYear = (year: string): void => {
        const yearNum = parseInt(year, 10)
        const newDateRange = {
            ...this.state.config.dateRange,
            toYear: yearNum,
        }
        const newConfig = {
            ...this.state.config,
            dateRange: newDateRange,
        }
        this.setState({
            config: newConfig,
        })
    }

    updateToMonth = (month: string): void => {
        const monthNum = parseInt(month, 10)
        const newDateRange = {
            ...this.state.config.dateRange,
            toMonth: monthNum,
        }
        const newConfig = {
            ...this.state.config,
            dateRange: newDateRange,
        }
        this.setState({
            config: newConfig,
        })
    }

    toggleSettings = (): void => {
        this.setState({ areSettingsShown: !this.state.areSettingsShown })
    }

    updateSearchTermsText = (value: string): void => {
        // Parse all terms from text input

        this.setState({
            textFieldContent: value,
        })
    }

    saveSearchTerms = async (): Promise<void> => {
        const allEnteredTerms = this.state.textFieldContent
            .split('\n')
            .map((term) => term.trim())
            .filter((term) => term !== '')

        // Combine language terms with user-added terms using Set to avoid duplicates
        const termsSet = Array.from(new Set([...allEnteredTerms])).join(' ')
        this.setState({
            config: {
                ...this.state.config,
                terms: termsSet,
            },
        })

        await this.saveSearchTermsToSettings(termsSet)
    }

    toggleLanguage = async (language: LanguageKey): Promise<void> => {
        const currentLanguages = this.state.config.selectedLanguages
        let newLanguages: LanguageKey[]

        if (currentLanguages.includes(language)) {
            // Remove language if it's currently selected
            newLanguages = currentLanguages.filter((lang) => lang !== language)
        } else {
            // Add language if it's not currently selected
            newLanguages = [...currentLanguages, language]
        }

        // Ensure at least one language is always selected
        if (newLanguages.length === 0) {
            newLanguages = ['en']
        }

        // Generate terms for new language selection
        const newLanguageTerms = this.generateTermsFromLanguages(newLanguages)

        // Get all current language-generated terms (from currently selected languages)
        const currentLanguageTerms =
            this.generateTermsFromLanguages(currentLanguages)

        // Separate user-added terms from language-generated terms
        // User-added terms are those that exist in config but not in current language terms
        const userAddedTerms = this.state.config.terms
            .split(' ')
            .filter((term) => !currentLanguageTerms.includes(term))

        // Combine new language terms with user-added terms using Set to avoid duplicates
        const termsSet = new Set([...newLanguageTerms, ...userAddedTerms])
        const finalTerms = Array.from(termsSet).sort()
        const finalTermsString = finalTerms.join(' OR ')

        // Update config with new languages and terms
        this.setState({
            config: {
                ...this.state.config,
                selectedLanguages: newLanguages,
                terms: finalTermsString,
            },
            textFieldContent: finalTerms.join('\n'),
        })

        await this.saveSearchTermsToSettings(finalTermsString)
    }
}
